import React, { useCallback, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import styled, { withTheme } from "styles/styled_components";
import { Maybe } from "true-myth";

import Icon, { ICON_TYPE, ICON_COLOR } from "components/shared/icon";
import { StyledTimeBlock, BORDERS } from "components/schedule/time_block";

import { OpenBlockFunc } from "actions/scheduling/blocks";
import { IThemeInterface } from "styles/theme";
import { IPerson } from "types/schedules";

interface IAddShiftButton extends IAddShiftButtonProps {
  border?: BORDERS;
  date: Date;
  delayHover?: boolean;
  defaultHour?: number;
  openBlockModal: OpenBlockFunc;
  person: IPerson;
  theme: IThemeInterface;
}

interface IAddShiftButtonProps {
  numShifts: number;
  isHovered?: boolean;
}

const StyledAddShiftButton = styled(StyledTimeBlock)<IAddShiftButtonProps>`
  align-items: center;
  background: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  min-height: ${({ theme }) => theme.rawShiftHeight}px;
  height: ${({ numShifts, theme }) =>
    getButtonHeight(numShifts, theme.rawShiftHeight)};
  padding: 0;

  ${({ isHovered }) => {
    return !isHovered ? "border-width: 0;" : null;
  }}
  &:not(:hover) > * {
    visibility: hidden;
  }
`;

function getButtonHeight(numShifts: number, shiftHeight: number): string {
  if (numShifts === 0) {
    return "100%";
  } else {
    return `calc(100% - ${numShifts * shiftHeight}px)`;
  }
}

const HOVER_DELAY = 250;

function AddShiftButton({
  border,
  date,
  delayHover,
  defaultHour,
  numShifts,
  openBlockModal,
  person,
  theme,
}: IAddShiftButton) {
  const [isHovered, setHover] = useState(false);
  const delayedHover = useMemo(
    () => (delayHover ? debounce(setHover, HOVER_DELAY) : setHover),
    [setHover]
  );

  const onClick = useCallback(
    () =>
      openBlockModal({
        existingBlock: Maybe.nothing(),
        person,
        date,
        defaultHour,
      }),
    [person, date, defaultHour]
  );

  return (
    <StyledAddShiftButton
      border={border}
      isHovered={isHovered}
      role="button"
      numShifts={numShifts}
      onClick={onClick}
      onMouseEnter={() => delayedHover(true)}
      onMouseLeave={() => delayedHover(false)}>
      {isHovered && (
        <Icon
          type={ICON_TYPE.PLUS_SIGN}
          color={ICON_COLOR.BLUE}
          size={theme.iconSize.small}
          lightenOnHover
        />
      )}
    </StyledAddShiftButton>
  );
}

export default withTheme(AddShiftButton);
